import React, { useMemo } from "react";
import {
  TagPicker,
  TagPickerControl,
  TagPickerGroup,
  TagPickerInput,
  TagPickerList,
  TagPickerOption,
} from "@fluentui/react-tag-picker";
import { Skeleton, SkeletonItem, Tag, makeStyles } from "@fluentui/react-components";
import {
  tagPickerListPositioning,
  useTagPickerOptionLayoutStyles,
} from "./TagPickerOptionLayout";

const useStyles = makeStyles({
  list: {
    maxHeight: "300px",
    overflowY: "auto",
  },
});

export interface SourceSingleTagPickerOption {
  id: string;
  label: string;
  secondaryLabel?: string;
}

interface SourceSingleTagPickerProps {
  selectedId?: string;
  selectedLabel?: string;
  query: string;
  placeholder: string;
  options: SourceSingleTagPickerOption[];
  onQueryChange: (value: string) => void;
  onSelect: (id?: string) => void;
  clearSelectionText: string;
  disabled?: boolean;
  isLoading?: boolean;
}

export const SourceSingleTagPicker: React.FC<SourceSingleTagPickerProps> = ({
  selectedId,
  selectedLabel,
  query,
  placeholder,
  options,
  onQueryChange,
  onSelect,
  clearSelectionText,
  disabled,
  isLoading,
}) => {
  const styles = useStyles();
  const optionLayout = useTagPickerOptionLayoutStyles();
  const clearSelectionValue = "__clearSelection";
  const normalizedQuery = query.trim().toLowerCase();

  const selectedOptions = useMemo(
    () => (selectedId ? [selectedId] : []),
    [selectedId],
  );
  const filteredOptions = useMemo(
    () =>
      options
        .filter((option) => option.id !== selectedId)
        .filter((option) =>
          normalizedQuery.length === 0
            ? true
            : option.label.toLowerCase().includes(normalizedQuery),
        ),
    [options, selectedId, normalizedQuery],
  );

  if (isLoading) {
    return (
      <Skeleton>
        <SkeletonItem shape="rectangle" size={32} />
      </Skeleton>
    );
  }

  return (
    <TagPicker
      positioning={tagPickerListPositioning}
      selectedOptions={selectedOptions}
      disabled={disabled}
      // Picking an option ends the search, so the term goes with it. Leaving
      // it behind stranded a half-typed word next to the tag it had already
      // found, and kept the list filtered to that word next time the picker
      // was opened.
      onOptionSelect={(_, data) => {
        onQueryChange("");

        if (data.value === clearSelectionValue || !data.selectedOptions.includes(data.value)) {
          onSelect(undefined);
          return;
        }
        onSelect(data.value);
      }}
    >
      <TagPickerControl>
        <TagPickerGroup>
          {/* While a new search is typed, the selection it may replace is hidden. */}
          {selectedId && selectedLabel && query === "" ? (
            <Tag value={selectedId}>{selectedLabel}</Tag>
          ) : null}
        </TagPickerGroup>
        <TagPickerInput
          value={query}
          placeholder={selectedId ? "" : placeholder}
          onChange={(event) => {
            onQueryChange(event.currentTarget.value);
          }}
        />
      </TagPickerControl>
      <TagPickerList className={styles.list}>
        {selectedId ? (
          <TagPickerOption
            text={clearSelectionText}
            value={clearSelectionValue}
          >
            {clearSelectionText}
          </TagPickerOption>
        ) : null}
        {filteredOptions.map((option) => (
          <TagPickerOption
            key={option.id}
            value={option.id}
            text={option.label}
            className={optionLayout.option}
            secondaryContent={
              option.secondaryLabel
                ? { children: option.secondaryLabel, className: optionLayout.secondaryContent }
                : undefined
            }
          >
            {option.label}
          </TagPickerOption>
        ))}
      </TagPickerList>
    </TagPicker>
  );
};
